/**
 * PokerAdminPanel — Phase 3 master admin console for the Poker Room.
 *
 * 5 sections in one card (tab strip at the top):
 *   1. Live Tables — pause / kick / close, view seated players
 *   2. Hand History — recent hands with .txt export (per-hand + bulk)
 *   3. Private Requests — approve/deny player-created private tables
 *   4. Moderation — mute/unmute players, delete chat messages
 *   5. Audit Log — every admin action logged
 *
 * Read/write via REST — no WebSocket dependency here. For live "watch a
 * hand" spectator, see the "Watch" button which opens PokerAdminSpectator.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import setIfChanged from '../../lib/setIfChanged';
import { toast } from 'sonner';
import {
    Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
    Pause, Play, UserX, XCircle, Download, Eye, Ban, MicOff,
    Trash2, CheckCircle, Shield, RefreshCw, KeyRound, Target,
} from 'lucide-react';

import PokerBountyPanel from './PokerBountyPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ withCredentials: true });
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtTs = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { timeZone: 'America/Chicago' }); } catch { return iso; }
};

const TABS = [
    { id: 'tables', label: 'Live Tables', icon: Shield },
    { id: 'hands', label: 'Hand History', icon: Download },
    { id: 'bounties', label: 'Bounties', icon: Target },
    { id: 'private', label: 'Private Requests', icon: KeyRound },
    { id: 'mod', label: 'Moderation', icon: MicOff },
    { id: 'audit', label: 'Audit Log', icon: Eye },
];

// ──────────────────────────────────────────────────────────────────────────
// Table subpanel
// ──────────────────────────────────────────────────────────────────────────
function TablesPanel() {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [kickModal, setKickModal] = useState(null);   // { tid, user_id, name }
    const [pauseModal, setPauseModal] = useState(null); // { tid, name }
    const [confirmClose, setConfirmClose] = useState(null); // { tid, name }
    const [pauseReason, setPauseReason] = useState('');
    const [kickReason, setKickReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/poker-room/admin/tables`, auth());
            setTables((prev) => setIfChanged(prev, res.data.tables || []));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load tables');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const tick = () => { if (!document.hidden) load(); };
        const t = setInterval(tick, 6000);
        return () => clearInterval(t);
    }, [load]);

    const doPause = async (paused) => {
        try {
            await axios.post(`${API}/poker-room/admin/tables/${pauseModal.tid}/pause`,
                { paused, reason: pauseReason }, auth());
            toast.success(paused ? 'Table paused' : 'Table resumed');
            setPauseModal(null);
            setPauseReason('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };
    const doKick = async () => {
        try {
            const res = await axios.post(`${API}/poker-room/admin/tables/${kickModal.tid}/kick`,
                { user_id: kickModal.user_id, reason: kickReason }, auth());
            toast.success(`Kicked · $${Number(res.data.refunded || 0).toFixed(2)} refunded`);
            setKickModal(null);
            setKickReason('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };
    const doClose = async () => {
        try {
            await axios.post(`${API}/poker-room/admin/tables/${confirmClose.tid}/close`,
                {}, auth());
            toast.success('Table closed and stacks refunded');
            setConfirmClose(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <div className="space-y-3" data-testid="poker-admin-tables">
            <div className="flex items-center justify-between">
                <div className="text-xs text-white/60">
                    {tables.length} table{tables.length === 1 ? '' : 's'} · live pause/kick/close controls
                </div>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {tables.map((t) => (
                    <div key={t.id}
                         className={`rounded-xl border p-3 ${
                             t.closed ? 'border-red-500/50 bg-red-950/30'
                             : t.paused ? 'border-orange-500/50 bg-orange-950/30'
                             : t.private ? 'border-purple-500/40 bg-purple-950/20'
                             : 'border-emerald-500/30 bg-black/30'
                         }`}
                         data-testid={`poker-admin-table-${t.id}`}>
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <div className="font-black text-sm flex items-center gap-2 flex-wrap">
                                    {t.name}
                                    {t.private && <Badge className="bg-purple-500/40 text-purple-100 text-[9px]">PRIVATE</Badge>}
                                    {t.paused && !t.closed && <Badge className="bg-orange-500/40 text-orange-100 text-[9px]">PAUSED</Badge>}
                                    {t.closed && <Badge className="bg-red-500/40 text-red-100 text-[9px]">CLOSED</Badge>}
                                </div>
                                <div className="text-[10px] text-white/50 mt-0.5">
                                    ${t.sb}/${t.bb} · {t.seated}/{t.max_seats} seated · {t.hands_played} hands · avg pot {money(t.avg_pot)}
                                </div>
                                {t.private && t.invite_code && (
                                    <div className="text-[10px] font-mono text-purple-200 mt-1">
                                        Invite: {t.invite_code}
                                    </div>
                                )}
                                {t.paused && t.pause_reason && (
                                    <div className="text-[10px] text-orange-200 mt-1 italic">"{t.pause_reason}"</div>
                                )}
                            </div>
                        </div>

                        {/* Seated players */}
                        {t.seats && t.seats.length > 0 && (
                            <div className="mb-2 space-y-1 max-h-40 overflow-y-auto">
                                {t.seats.map((s) => (
                                    <div key={s.seat_no} className="flex items-center justify-between text-[11px] bg-black/40 rounded px-2 py-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-white/40 w-5">#{s.seat_no}</span>
                                            <span className="font-bold truncate max-w-[140px]">
                                                {s.display_name}
                                                {s.is_bot && <span className="text-white/50"> 🤖</span>}
                                            </span>
                                            <span className="text-emerald-300 font-mono">{money(s.stack)}</span>
                                        </div>
                                        {!s.is_bot && (
                                            <button
                                                onClick={() => setKickModal({ tid: t.id, user_id: s.user_id, name: s.display_name })}
                                                className="text-red-400 hover:text-red-300"
                                                title="Kick from table"
                                                data-testid={`poker-admin-kick-${t.id}-${s.seat_no}`}
                                            >
                                                <UserX className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-2">
                            {!t.closed && (
                                <Button size="sm" variant="outline"
                                        className={t.paused ? 'border-emerald-500/40 text-emerald-300' : 'border-orange-500/40 text-orange-300'}
                                        onClick={() => setPauseModal({ tid: t.id, name: t.name, currently: t.paused })}
                                        data-testid={`poker-admin-pause-${t.id}`}>
                                    {t.paused ? <><Play className="w-3 h-3 mr-1" /> Resume</> : <><Pause className="w-3 h-3 mr-1" /> Pause</>}
                                </Button>
                            )}
                            {!t.closed && (
                                <Button size="sm" variant="outline"
                                        className="border-red-500/40 text-red-300"
                                        onClick={() => setConfirmClose({ tid: t.id, name: t.name })}
                                        data-testid={`poker-admin-close-${t.id}`}>
                                    <XCircle className="w-3 h-3 mr-1" /> Close
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Pause dialog */}
            <Dialog open={!!pauseModal} onOpenChange={(v) => !v && setPauseModal(null)}>
                <DialogContent data-testid="poker-admin-pause-dialog">
                    <DialogHeader>
                        <DialogTitle>{pauseModal?.currently ? 'Resume' : 'Pause'} {pauseModal?.name}</DialogTitle>
                        <DialogDescription>
                            {pauseModal?.currently
                                ? 'Resuming will restart the timer + bot autopilot. New hands can be dealt again.'
                                : 'Pausing stops the timer, halts bot autopilot, and blocks all player actions until you resume.'}
                        </DialogDescription>
                    </DialogHeader>
                    {!pauseModal?.currently && (
                        <div>
                            <label className="text-xs text-white/60">Reason shown to players</label>
                            <Input value={pauseReason} onChange={(e) => setPauseReason(e.target.value)}
                                   placeholder="Investigating a disputed hand…"
                                   data-testid="poker-admin-pause-reason" />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPauseModal(null)}>Cancel</Button>
                        <Button onClick={() => doPause(!pauseModal.currently)}
                                data-testid="poker-admin-pause-confirm">
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Kick dialog */}
            <Dialog open={!!kickModal} onOpenChange={(v) => !v && setKickModal(null)}>
                <DialogContent data-testid="poker-admin-kick-dialog">
                    <DialogHeader>
                        <DialogTitle>Kick {kickModal?.name}?</DialogTitle>
                        <DialogDescription>
                            They will be stood up from the table and their remaining stack will be refunded to their wallet immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <Input value={kickReason} onChange={(e) => setKickReason(e.target.value)}
                           placeholder="Reason (shown at table)"
                           data-testid="poker-admin-kick-reason" />
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setKickModal(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={doKick}
                                data-testid="poker-admin-kick-confirm">
                            <UserX className="w-3 h-3 mr-1" /> Kick
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close dialog */}
            <Dialog open={!!confirmClose} onOpenChange={(v) => !v && setConfirmClose(null)}>
                <DialogContent data-testid="poker-admin-close-dialog">
                    <DialogHeader>
                        <DialogTitle>Close {confirmClose?.name}?</DialogTitle>
                        <DialogDescription>
                            All players will be stood up and their stacks refunded. Private tables will be removed from the lobby entirely.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmClose(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={doClose}
                                data-testid="poker-admin-close-confirm">
                            <XCircle className="w-3 h-3 mr-1" /> Close Table
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Hand history + export
// ──────────────────────────────────────────────────────────────────────────
function HandsPanel() {
    const [hands, setHands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = filter ? `?table_id=${encodeURIComponent(filter)}` : '';
            const res = await axios.get(`${API}/poker-room/admin/hands${q}`, auth());
            setHands(res.data.hands || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const downloadOne = async (hid) => {
        const tok = localStorage.getItem('access_token');
        const url = `${API}/poker-room/admin/hands/${hid}/export`;
        try {
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${tok}` },
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'text/plain' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `hand_${hid}.txt`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (e) {
            toast.error('Export failed');
        }
    };
    const downloadBulk = async () => {
        const tok = localStorage.getItem('access_token');
        const q = filter ? `&table_id=${encodeURIComponent(filter)}` : '';
        try {
            const res = await axios.get(
                `${API}/poker-room/admin/hands-export-bulk?limit=100${q}`,
                { headers: { Authorization: `Bearer ${tok}` }, responseType: 'blob' },
            );
            const blob = new Blob([res.data], { type: 'text/plain' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `betohalics_hands_${Date.now()}.txt`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success('Bulk export downloaded');
        } catch (e) {
            toast.error('Bulk export failed');
        }
    };

    return (
        <div className="space-y-3" data-testid="poker-admin-hands">
            <div className="flex flex-wrap items-center gap-2">
                <Input placeholder="Filter by table id (blank = all)"
                       value={filter}
                       onChange={(e) => setFilter(e.target.value)}
                       className="max-w-[280px]"
                       data-testid="poker-admin-hands-filter" />
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button size="sm" onClick={downloadBulk}
                        className="bg-emerald-600 hover:bg-emerald-500"
                        data-testid="poker-admin-hands-bulk-export">
                    <Download className="w-3 h-3 mr-1" /> Bulk Export (.txt)
                </Button>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-black/40 text-white/50 uppercase tracking-widest">
                        <tr>
                            <th className="text-left px-2 py-2">Table</th>
                            <th className="text-left px-2 py-2">Hand</th>
                            <th className="text-right px-2 py-2">Pot</th>
                            <th className="text-right px-2 py-2">Rake</th>
                            <th className="text-left px-2 py-2">Winner</th>
                            <th className="text-left px-2 py-2">Completed</th>
                            <th className="px-2 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {hands.length === 0 && (
                            <tr><td colSpan="7" className="text-center text-white/40 py-6">No hands found.</td></tr>
                        )}
                        {hands.map((h) => (
                            <tr key={h.hand_id} className="border-t border-white/5 hover:bg-white/5">
                                <td className="px-2 py-1.5 font-mono text-white/70">{h.table_id}</td>
                                <td className="px-2 py-1.5 font-mono text-white/50 text-[10px]">{h.hand_id.slice(0, 10)}…</td>
                                <td className="px-2 py-1.5 text-right font-bold text-emerald-300">{money(h.pot)}</td>
                                <td className="px-2 py-1.5 text-right text-yellow-300/80">{money(h.rake)}</td>
                                <td className="px-2 py-1.5">
                                    {(h.winners || []).map((w, i) => (
                                        <div key={i} className="text-[10px]">
                                            Seat {w.seat} · {money(w.amount)} · <span className="text-white/50">{w.hand_label}</span>
                                        </div>
                                    ))}
                                </td>
                                <td className="px-2 py-1.5 text-white/50 text-[10px]">{fmtTs(h.completed_at)}</td>
                                <td className="px-2 py-1.5 text-right">
                                    <Button variant="ghost" size="sm" onClick={() => downloadOne(h.hand_id)}
                                            data-testid={`poker-admin-hand-export-${h.hand_id}`}>
                                        <Download className="w-3 h-3" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Private-table requests
// ──────────────────────────────────────────────────────────────────────────
function PrivateRequestsPanel() {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(false);
    const [denyModal, setDenyModal] = useState(null);
    const [denyReason, setDenyReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/poker-room/admin/private-tables/pending`, auth());
            setPending(res.data.pending || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    const approve = async (id) => {
        try {
            const res = await axios.post(`${API}/poker-room/admin/private-tables/${id}/approve`,
                {}, auth());
            toast.success(`Approved · code ${res.data.invite_code}`, { duration: 8000 });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };
    const deny = async () => {
        try {
            await axios.post(`${API}/poker-room/admin/private-tables/${denyModal.id}/deny`,
                { reason: denyReason }, auth());
            toast.success('Request denied');
            setDenyModal(null);
            setDenyReason('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <div className="space-y-3" data-testid="poker-admin-private">
            <div className="flex items-center justify-between">
                <div className="text-xs text-white/60">
                    {pending.length} pending request{pending.length === 1 ? '' : 's'}
                </div>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>
            {pending.length === 0 && (
                <div className="text-center text-white/40 py-8 text-xs italic">
                    No pending private-table requests.
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {pending.map((r) => (
                    <div key={r.id} className="rounded-xl border border-purple-500/30 bg-purple-950/20 p-3"
                         data-testid={`poker-admin-private-req-${r.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-black text-sm">{r.name}</div>
                                <div className="text-[10px] text-white/60 mt-0.5">
                                    ${r.sb}/${r.bb} · {r.max_seats}-max
                                </div>
                                <div className="text-[10px] text-purple-200 mt-1">
                                    Requested by <span className="font-bold">{r.creator_name}</span> · {fmtTs(r.created_at)}
                                </div>
                                {r.note && (
                                    <div className="text-[11px] text-white/70 mt-2 italic">"{r.note}"</div>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                            <Button size="sm" onClick={() => approve(r.id)}
                                    className="bg-emerald-600 hover:bg-emerald-500"
                                    data-testid={`poker-admin-private-approve-${r.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="destructive"
                                    onClick={() => setDenyModal(r)}
                                    data-testid={`poker-admin-private-deny-${r.id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Deny
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!denyModal} onOpenChange={(v) => !v && setDenyModal(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deny request?</DialogTitle>
                        <DialogDescription>
                            Reason will be logged. The player can request again.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)}
                              placeholder="Reason (optional)"
                              data-testid="poker-admin-deny-reason" />
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDenyModal(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={deny}
                                data-testid="poker-admin-deny-confirm">Deny</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Moderation — mute/ban lookup by user id
// ──────────────────────────────────────────────────────────────────────────
function ModerationPanel() {
    const [userId, setUserId] = useState('');
    const [muteMinutes, setMuteMinutes] = useState(30);
    const [reason, setReason] = useState('');
    const [banDays, setBanDays] = useState('');

    const mute = async () => {
        if (!userId.trim()) { toast.error('User ID required'); return; }
        try {
            await axios.post(`${API}/poker-room/admin/chat/mute`, {
                user_id: userId.trim(), minutes: Number(muteMinutes) || 30, reason,
            }, auth());
            toast.success(`Muted for ${muteMinutes} min`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const unmute = async () => {
        if (!userId.trim()) { toast.error('User ID required'); return; }
        try {
            await axios.delete(`${API}/poker-room/admin/chat/mute/${userId.trim()}`, auth());
            toast.success('Unmuted');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const ban = async () => {
        if (!userId.trim()) { toast.error('User ID required'); return; }
        try {
            await axios.post(`${API}/poker-room/admin/players/${userId.trim()}/ban`, {
                reason, days: banDays ? Number(banDays) : null,
            }, auth());
            toast.success(banDays ? `Banned for ${banDays} days` : 'Banned permanently');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const unban = async () => {
        if (!userId.trim()) { toast.error('User ID required'); return; }
        try {
            await axios.delete(`${API}/poker-room/admin/players/${userId.trim()}/ban`, auth());
            toast.success('Ban lifted');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    return (
        <div className="space-y-4" data-testid="poker-admin-mod">
            <div>
                <label className="text-[11px] uppercase tracking-widest text-white/60 font-black">
                    Player Mongo ID
                </label>
                <Input value={userId} onChange={(e) => setUserId(e.target.value)}
                       placeholder="e.g. 6a1234…"
                       data-testid="poker-admin-mod-userid" />
                <p className="text-[10px] text-white/40 mt-1">
                    Get from Users admin tab or from the chat panel when reviewing a message.
                </p>
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-widest text-white/60 font-black">Reason</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="Reason (logged in audit)"
                       data-testid="poker-admin-mod-reason" />
            </div>

            {/* Mute row */}
            <div className="rounded-xl border border-orange-500/30 bg-orange-950/20 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <MicOff className="w-4 h-4 text-orange-300" />
                    <span className="font-black text-sm text-orange-200">Chat Mute</span>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="number" value={muteMinutes}
                           onChange={(e) => setMuteMinutes(e.target.value)}
                           className="max-w-[100px]" min="1" max="1440"
                           data-testid="poker-admin-mute-mins" />
                    <span className="text-xs text-white/60">min</span>
                    <Button size="sm" onClick={mute} className="bg-orange-600 hover:bg-orange-500"
                            data-testid="poker-admin-mute-btn">
                        Mute
                    </Button>
                    <Button size="sm" variant="ghost" onClick={unmute}
                            data-testid="poker-admin-unmute-btn">
                        Unmute
                    </Button>
                </div>
            </div>

            {/* Ban row */}
            <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Ban className="w-4 h-4 text-red-300" />
                    <span className="font-black text-sm text-red-200">Poker Room Ban</span>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="number" value={banDays}
                           onChange={(e) => setBanDays(e.target.value)}
                           className="max-w-[100px]" min="1"
                           placeholder="days"
                           data-testid="poker-admin-ban-days" />
                    <span className="text-xs text-white/60">days (blank = permanent)</span>
                    <Button size="sm" variant="destructive" onClick={ban}
                            data-testid="poker-admin-ban-btn">
                        Ban
                    </Button>
                    <Button size="sm" variant="ghost" onClick={unban}
                            data-testid="poker-admin-unban-btn">
                        Lift Ban
                    </Button>
                </div>
                <p className="text-[10px] text-white/40 mt-2">
                    Banning immediately kicks the player from any active tables and refunds their stack.
                </p>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Audit log
// ──────────────────────────────────────────────────────────────────────────
function AuditPanel() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/poker-room/admin/audit-log?limit=100`, auth());
            setEntries(res.data.entries || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3" data-testid="poker-admin-audit">
            <div className="flex items-center justify-between">
                <div className="text-xs text-white/60">
                    Last {entries.length} actions
                </div>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-black/40 text-white/50 uppercase tracking-widest">
                        <tr>
                            <th className="text-left px-2 py-2">When</th>
                            <th className="text-left px-2 py-2">Actor</th>
                            <th className="text-left px-2 py-2">Action</th>
                            <th className="text-left px-2 py-2">Target</th>
                            <th className="text-left px-2 py-2">Meta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 && (
                            <tr><td colSpan="5" className="text-center text-white/40 py-6 italic">
                                No audit entries yet.
                            </td></tr>
                        )}
                        {entries.map((e) => (
                            <tr key={e._id} className="border-t border-white/5">
                                <td className="px-2 py-1.5 text-white/50 text-[10px]">{fmtTs(e.ts)}</td>
                                <td className="px-2 py-1.5 font-bold">{e.actor_name || '—'}</td>
                                <td className="px-2 py-1.5">
                                    <Badge className="bg-emerald-500/20 text-emerald-200 text-[9px]">
                                        {e.action}
                                    </Badge>
                                </td>
                                <td className="px-2 py-1.5 text-white/70 text-[11px]">
                                    {e.target_table_id || e.target_user_name || e.target_user_id || '—'}
                                </td>
                                <td className="px-2 py-1.5 text-white/50 text-[10px] font-mono max-w-[240px] truncate">
                                    {e.meta ? JSON.stringify(e.meta) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Main panel with tab strip
// ──────────────────────────────────────────────────────────────────────────
export default function PokerAdminPanel() {
    const [tab, setTab] = useState('tables');

    return (
        <Card className="bg-black/40 border-emerald-500/30" data-testid="poker-admin-panel">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" />
                    Poker Room — Admin Controls
                </CardTitle>
                <p className="text-xs text-white/60">
                    Pause/kick/close · Hand history export · Private-table approvals · Moderation · Audit log
                </p>
            </CardHeader>
            <CardContent>
                <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id}
                                    onClick={() => setTab(t.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black whitespace-nowrap transition-all ${
                                        tab === t.id
                                            ? 'bg-emerald-500 text-black shadow-[0_0_18px_rgba(52,211,153,0.5)]'
                                            : 'bg-black/40 text-white/60 hover:text-white'
                                    }`}
                                    data-testid={`poker-admin-tab-${t.id}`}>
                                <Icon className="w-3.5 h-3.5" />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                {tab === 'tables' && <TablesPanel />}
                {tab === 'hands' && <HandsPanel />}
                {tab === 'bounties' && <PokerBountyPanel />}
                {tab === 'private' && <PrivateRequestsPanel />}
                {tab === 'mod' && <ModerationPanel />}
                {tab === 'audit' && <AuditPanel />}
            </CardContent>
        </Card>
    );
}
